import { Component, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	SetupWizardUnitFormPayload,
	UNIT_CSV_LABELS,
	UnitCsvKeys,
	UnitCsvTemplate,
	UNIT_CSV_SPEC,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TextField } from '../../../fields/setup-wizard/text-field/text-field';
import { CheckboxField } from '../../../fields/setup-wizard/checkbox-field/checkbox-field';
import { IntegerField } from '../../../fields/setup-wizard/integer-field/integer-field';
import { LanguageDropdownField } from '../../../fields/setup-wizard/language-dropdown-field/language-dropdown-field';
import { TextareaField } from '../../../fields/setup-wizard/textarea-field/textarea-field';
import { FormUtil } from '../../../../utils/form.util';

type UnitForm = {
	[UnitCsvKeys.ssoUnitId]: FormControl<string | null>;
	[UnitCsvKeys.ssoCourseId]: FormControl<string | null>;
	[UnitCsvKeys.defaultLangId]: FormControl<string | null>;
	[UnitCsvKeys.displayOrder]: FormControl<number | null>;
	[UnitCsvKeys.enName]: FormControl<string | null>;
	[UnitCsvKeys.enImageUri]: FormControl<string | null>;
	[UnitCsvKeys.enDescriptionShort]: FormControl<string | null>;
	[UnitCsvKeys.enDescriptionHtml]: FormControl<string | null>;
	[UnitCsvKeys.esName]: FormControl<string | null>;
	[UnitCsvKeys.esImageUri]: FormControl<string | null>;
	[UnitCsvKeys.esDescriptionShort]: FormControl<string | null>;
	[UnitCsvKeys.esDescriptionHtml]: FormControl<string | null>;
	[UnitCsvKeys.unitUuid]: FormControl<string | null>;
	[UnitCsvKeys.courseUuid]: FormControl<string | null>;
	[UnitCsvKeys.active]: FormControl<boolean | null>;
};

@Component({
	selector: 'app-setup-wizard-unit-form',
	imports: [
		ReactiveFormsModule,
		NzButtonModule,
		NzFormModule,
		NzInputModule,
		NzSpinModule,
		TextField,
		CheckboxField,
		IntegerField,
		LanguageDropdownField,
		TextareaField,
	],
	standalone: true,
	templateUrl: './setup-wizard-unit-form.html',
	styleUrl: './setup-wizard-unit-form.scss',
})
export class SetupWizardUnitForm {
	public courseUuid = input.required<string>();
	public edit = input<UnitCsvTemplate | null>(null);
	public loading = input<boolean>(false);
	public onClear = output<void>();
	public onSubmit = output<SetupWizardUnitFormPayload>();
	public UnitCsvKeys = UnitCsvKeys;
	public UNIT_CSV_LABELS = UNIT_CSV_LABELS;
	public Validators = Validators;

	public form = new FormGroup<UnitForm>({
		[UnitCsvKeys.ssoUnitId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.ssoCourseId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.defaultLangId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.displayOrder]: new FormControl<number | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.enName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.enImageUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.enDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.enDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.esName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.esImageUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.esDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.esDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.unitUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.courseUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[UnitCsvKeys.active]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
	});

	constructor() {
		FormUtil.applyCsvSpecValidators(this.form, UNIT_CSV_SPEC);
	}

	ngOnChanges() {
		const edit = this.edit();

		if (edit) {
			// If edit contains a unitUuid we can remove all validators for ssoUnitId
			if (edit[UnitCsvKeys.unitUuid]) {
				this.form.get(UnitCsvKeys.ssoUnitId)?.clearValidators();
				this.form.get(UnitCsvKeys.ssoUnitId)?.updateValueAndValidity();
			}

			// If edit contains a courseUuid we can remove all validators for ssoCourseId
			if (edit[UnitCsvKeys.courseUuid]) {
				this.form.get(UnitCsvKeys.ssoCourseId)?.clearValidators();
				this.form.get(UnitCsvKeys.ssoCourseId)?.updateValueAndValidity();
			}

			this.form.patchValue({
				[UnitCsvKeys.ssoUnitId]: edit[UnitCsvKeys.ssoUnitId],
				[UnitCsvKeys.ssoCourseId]: edit[UnitCsvKeys.ssoCourseId],
				[UnitCsvKeys.defaultLangId]: edit[UnitCsvKeys.defaultLangId],
				[UnitCsvKeys.displayOrder]: edit[UnitCsvKeys.displayOrder],
				[UnitCsvKeys.enName]: edit[UnitCsvKeys.enName],
				[UnitCsvKeys.enImageUri]: edit[UnitCsvKeys.enImageUri],
				[UnitCsvKeys.enDescriptionShort]: edit[UnitCsvKeys.enDescriptionShort],
				[UnitCsvKeys.enDescriptionHtml]: edit[UnitCsvKeys.enDescriptionHtml],
				[UnitCsvKeys.esName]: edit[UnitCsvKeys.esName],
				[UnitCsvKeys.esImageUri]: edit[UnitCsvKeys.esImageUri],
				[UnitCsvKeys.esDescriptionShort]: edit[UnitCsvKeys.esDescriptionShort],
				[UnitCsvKeys.esDescriptionHtml]: edit[UnitCsvKeys.esDescriptionHtml],
				[UnitCsvKeys.unitUuid]: edit[UnitCsvKeys.unitUuid],
				[UnitCsvKeys.courseUuid]: edit[UnitCsvKeys.courseUuid],
				[UnitCsvKeys.active]: edit[UnitCsvKeys.active],
			});
		} else {
			const courseUuid = this.courseUuid();

			if (!courseUuid) {
				throw new Error('Course UUID is required');
			}

			this.form.patchValue({
				[UnitCsvKeys.courseUuid]: this.courseUuid(),
			});
		}
	}

	onFormSubmit() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			this.onSubmit.emit({
				[UnitCsvKeys.ssoUnitId]: value[UnitCsvKeys.ssoUnitId] ?? undefined,
				[UnitCsvKeys.ssoCourseId]: value[UnitCsvKeys.ssoCourseId] ?? undefined,
				[UnitCsvKeys.defaultLangId]: value[UnitCsvKeys.defaultLangId] ?? undefined,
				[UnitCsvKeys.displayOrder]: value[UnitCsvKeys.displayOrder] ?? undefined,
				[UnitCsvKeys.enName]: value[UnitCsvKeys.enName] ?? undefined,
				[UnitCsvKeys.enImageUri]: value[UnitCsvKeys.enImageUri] ?? undefined,
				[UnitCsvKeys.enDescriptionShort]: value[UnitCsvKeys.enDescriptionShort] ?? undefined,
				[UnitCsvKeys.enDescriptionHtml]: value[UnitCsvKeys.enDescriptionHtml] ?? undefined,
				[UnitCsvKeys.esName]: value[UnitCsvKeys.esName] ?? undefined,
				[UnitCsvKeys.esImageUri]: value[UnitCsvKeys.esImageUri] ?? undefined,
				[UnitCsvKeys.esDescriptionShort]: value[UnitCsvKeys.esDescriptionShort] ?? undefined,
				[UnitCsvKeys.esDescriptionHtml]: value[UnitCsvKeys.esDescriptionHtml] ?? undefined,
				[UnitCsvKeys.unitUuid]: value[UnitCsvKeys.unitUuid] ?? undefined,
				[UnitCsvKeys.courseUuid]: value[UnitCsvKeys.courseUuid] ?? undefined,
				[UnitCsvKeys.active]: value[UnitCsvKeys.active] ?? undefined,
			});

			this.form.reset();
		} else {
			this.form.markAllAsTouched();
		}
	}

	onClearClicked() {
		this.onClear.emit();
	}
}
